import { useMutation, useQueryClient } from "@tanstack/react-query";
import { CACHE_KEY_TICKETS } from "../../../utils/constants";
import { TicketInterface } from "../../../models/TicketModule/Ticket";
import ticketService from "../../../services/ticketService/ticketService";

type Vars = {
  ticketId: string;
  patch: Partial<TicketInterface>;
};

interface Ctx {
  snapshots: Array<[readonly unknown[], TicketInterface[] | undefined]>;
}

const useUpdateKanbanTicket = () => {
  const queryClient = useQueryClient();

  return useMutation<TicketInterface, Error, Vars, Ctx>({
    mutationFn: async ({ ticketId, patch }) => {
      return ticketService.Update(ticketId, patch as TicketInterface);
    },

    onMutate: async ({ ticketId, patch }) => {
      await queryClient.cancelQueries({ queryKey: [CACHE_KEY_TICKETS] });

      const snapshots = queryClient.getQueriesData<TicketInterface[]>({
        queryKey: [CACHE_KEY_TICKETS],
      });

      snapshots.forEach(([key, old]) => {
        if (!old) return;

        queryClient.setQueryData<TicketInterface[]>(key, (prev) => {
          if (!prev) return prev;
          return prev.map((t) => (t.id === ticketId ? { ...t, ...patch } : t));
        });
      });

      return { snapshots };
    },

    onError: (_err, _vars, ctx) => {
      ctx?.snapshots.forEach(([key, data]) => queryClient.setQueryData(key, data));
    },

    onSettled: () => {
      // important: dueDate may change which week query it belongs to
      queryClient.invalidateQueries({ queryKey: [CACHE_KEY_TICKETS] });
    },
  });
};

export default useUpdateKanbanTicket;
